// File: frontend/src/hooks/useDashboardPrefetch.js
//
// Warm the dashboard's critical path while the operator is doing something else.
//
// ── WHY ─────────────────────────────────────────────────────────────────────
// Opening the dashboard is a chain of serial round trips, and none of them can
// start until the one before finishes:
//
//   shell paints → fetch the Dashboard route chunk → CORS preflight → GET core
//
// Measured on real mobile browsers, the gap between the shell painting and the
// money cards filling is p50 984 ms, of which the API itself is ~38 ms. The rest
// is that chain. See
// docs/production-findings/performance/ROUTER_DASHBOARD_LOAD_DELAY_INVESTIGATION_2026-08-25.md
//
// So this does the chain early, at idle, from the shell — where the operator is
// not waiting on it.
//
// ── WHY IT DOES NOT ADD TRAFFIC ─────────────────────────────────────────────
// The prefetched CORE response goes into dashboardService's scoped cache, and
// the Dashboard's own mount reads it (or joins the in-flight request). One GET,
// moved earlier — not a second one. That is asserted by test, because a prefetch
// that duplicates the request is strictly worse than no prefetch at all.
//
// ── WHY IT IS A REAL REQUEST, NOT A WARM-UP PING ────────────────────────────
// A dummy request whose only job is to make the browser cache a CORS preflight
// spends a round trip and produces nothing. Fetching the payload the dashboard
// actually needs warms the connection, the preflight, the server cache and the
// client cache — and leaves usable data behind.

import { useEffect, useRef } from "react";
import { deferIdle } from "../utils/deferIdle";
import { dashboardService } from "../services/api";

/**
 * @param {object}  opts
 * @param {boolean} opts.enabled     authenticated, and the shell is up
 * @param {boolean} opts.allowed     this user may actually open the dashboard
 * @param {string}  opts.routerId    the selected station
 * @param {string}  opts.from
 * @param {string}  opts.to
 */
export default function useDashboardPrefetch({ enabled, allowed, routerId, from, to }) {
  // Once per scope per mount. Without this, any re-render that changed a dep
  // would fire another one — the "prefetch on every render" failure.
  const doneRef = useRef(new Set());

  useEffect(() => {
    // Every one of these is a reason NOT to spend a request:
    //   · not authenticated yet — an unauthenticated call would 401, and worse,
    //     it would be a private-data request made before we know who is asking;
    //   · no permission to the page — never prefetch data this user may not see;
    //   · no scope — the dashboard refuses an unscoped request anyway.
    if (!enabled || !allowed || !routerId || !from || !to) return undefined;

    const scope = `${routerId}:${from}:${to}`;
    if (doneRef.current.has(scope)) return undefined;
    doneRef.current.add(scope);

    // Deferred, so the shell finishes its own critical work first. Making login
    // feel slower to make the dashboard feel faster is not a trade worth making.
    //
    // deferIdle does not hand back a canceller, so the unmount guard is a flag:
    // an operator who navigates away before the idle callback runs should not
    // have a request fired on their behalf.
    let cancelled = false;
    deferIdle(() => {
      if (cancelled) return;
      dashboardService
        .getCommandCenter({ router_id: routerId, from, to, sections: "core" })
        // A prefetch is an optimisation. If it fails, the dashboard makes its own
        // request on mount and the operator never learns this happened — which is
        // correct. A toast here would report a problem that is not one.
        .catch(() => {});
    });

    return () => { cancelled = true; };
  }, [enabled, allowed, routerId, from, to]);
}
